import { Box, Center, Flex, Text } from "@chakra-ui/react";
import { BoardBox } from "./BoardBox";
import { useGame } from "../../contexts/GameContext";
import { Clue, EventTypes, JeopardyStates } from "types";
import {
  AnimatePresence,
  motion,
} from "framer-motion";
import { TextFit } from "TextFit";
import { useEffect, useRef } from "react";
import { useTimer } from "react-timer-hook";
import { createPortal } from "react-dom";
import { useGameSounds } from "../../hooks/useGameSounds";
import { useElevenLabsTTS } from "../../hooks/useElevenLabsTTS";
import { JeopardyTimerBar } from "../shared/JeopardyTimerBar";

export type Props = {
  clue: Clue;
  renderDelay: number;
};

const timeToAnswer = 15;

export const ClueBox: React.FC<Props> = ({ clue, renderDelay }) => {
  const { gameState, sendEvent } = useGame();
  const { playDailyDouble, playTimesUp } = useGameSounds();
  const { playQuestionAudio, playAnswerReveal, playNobodyGotIt, isPlaying: isTTSPlaying } = useElevenLabsTTS();
  const isActive = gameState?.activeClue?.id === clue.id;
  const playerAnswering =
    gameState?.currentState === JeopardyStates.PlayerAnswering;
  const isActiveDailyDouble =
    isActive && gameState?.currentState === JeopardyStates.DailyDoubleWager;
  const isLastAnswered = gameState?.lastAnsweredClueId === clue.id;
  const { seconds, restart, pause, resume } = useTimer({
    expiryTimestamp: new Date(),
    onExpire: () => {
      if (isActive) {
        playTimesUp();
        sendEvent({ type: EventTypes.TIME_UP });
      }
    },
  });
  const wasAnswering = useRef(false);
  const hasPlayedDailyDouble = useRef(false);
  const hasPlayedAnswerAudio = useRef(false);

  useEffect(() => {
    if (gameState?.currentState === JeopardyStates.PlayerAnswering) {
      pause();
      wasAnswering.current = true;
    } else if (wasAnswering.current) {
      resume();
      wasAnswering.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState?.currentState]);

  // Play answer audio when clue times out or all players answer incorrectly
  // Uses "The answer was..." intro before reading the answer
  useEffect(() => {
    if (
      isActive &&
      clue.answered &&
      !hasPlayedAnswerAudio.current &&
      clue.answerAudioUrl &&
      (gameState?.currentState === JeopardyStates.CategorySelection ||
        gameState?.lastAnsweredClueId === clue.id)
    ) {
      hasPlayedAnswerAudio.current = true;
      // Play answer reveal with intro ("The answer was...")
      playAnswerReveal(clue.answerAudioUrl).catch(console.error);
    }
    // Reset when clue becomes inactive
    if (!isActive) {
      hasPlayedAnswerAudio.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clue.answered, gameState?.currentState, gameState?.lastAnsweredClueId, isActive, clue.answerAudioUrl]);

  // Track when this clue becomes active - play audio and start timer after audio completes
  useEffect(() => {
    if (isActive && !isActiveDailyDouble) {
      // Play question audio first, then start timer
      const startTimerAfterAudio = async () => {
        if (clue.questionAudioUrl) {
          // Notify that question audio is playing (disable buzzing)
          sendEvent({ type: EventTypes.SET_QUESTION_AUDIO_PLAYING, playing: true });
          await playQuestionAudio(clue.questionAudioUrl);
          // Notify that question audio is done (enable buzzing)
          sendEvent({ type: EventTypes.SET_QUESTION_AUDIO_PLAYING, playing: false });
        }
        // Start timer after audio completes (or immediately if no audio)
        restart(new Date(new Date().getTime() + timeToAnswer * 1000));
      };
      startTimerAfterAudio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, clue.questionAudioUrl]);

  // Play daily double sound when entering DailyDoubleWager state
  useEffect(() => {
    if (isActiveDailyDouble && !hasPlayedDailyDouble.current) {
      playDailyDouble();
      hasPlayedDailyDouble.current = true;
    } else if (!isActiveDailyDouble) {
      hasPlayedDailyDouble.current = false;
    }
  }, [isActiveDailyDouble, playDailyDouble]);

  // Render Daily Double in a portal to escape the scaled container
  if (isActiveDailyDouble) {
    return (
      <>
        {createPortal(
          <motion.div
            style={{
              position: "fixed",
              transformStyle: "preserve-3d",
              background: "blue",
              padding: "20px",
              zIndex: 1000,
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
            }}
            initial={{
              scale: 0,
            }}
            animate={{
              scale: [null, 1],
              rotateX: 1080,
            }}
            transition={{
              duration: 2,
            }}
          >
            <Center height="100%" width="100%">
              <Text textStyle="jeoparty" fontSize="30vh">
                Daily Double
              </Text>
            </Center>
          </motion.div>,
          document.body
        )}
        <BoardBox backgroundColor="jeopardy.blue" padding="25px">
          <Text textStyle="clueValue">${clue.value}</Text>
        </BoardBox>
      </>
    );
  }

  // Render active clue in a portal to escape the scaled container
  const activeClueContent = isActive ? (
    createPortal(
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        width="100vw"
        height="100vh"
        backgroundColor="jeopardy.clueBlue"
        zIndex={1000}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {/* Daily Double wager badge - positioned at top */}
        {clue.isDailyDouble && playerAnswering && (
          <Box
            position="absolute"
            top="20px"
            left="50%"
            transform="translateX(-50%)"
            bg="jeopardy.gold"
            color="black"
            px={6}
            py={2}
            borderRadius="md"
            zIndex={1003}
          >
            <Text fontSize="3vh" fontWeight="bold" textAlign="center">
              {gameState.activePlayer?.name}: ${gameState.dailyDoubleWager?.toLocaleString()}
            </Text>
          </Box>
        )}

        {/* Centered clue text */}
        <Box
          width="100%"
          height="100%"
          maxWidth="90vw"
          maxHeight="80vh"
          padding="60px"
          boxSizing="border-box"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <TextFit
            max={6}
            zIndex={1002}
            textStyle="clueActive"
            width="100%"
            height="100%"
            as={Center}
            textAlign="center"
          >
            {clue.question}
          </TextFit>
        </Box>

        {/* Jeopardy-style timer bars */}
        <JeopardyTimerBar secondsRemaining={seconds} totalSeconds={timeToAnswer} />
      </Box>,
      document.body
    )
  ) : null;

  return (
    <>
      {activeClueContent}
      <BoardBox
        backgroundColor={isActive ? "jeopardy.clueBlue" : "jeopardy.blue"}
        padding={isActive ? "25px" : "25px"}
        key={clue.id}
      >
        <AnimatePresence mode="wait">
          {isActive ? null : clue.answered ? (
            <TextFit
              key={`answer-${clue.id}`}
              max={2.5}
              min={0.5}
              as={Center}
              opacity={isLastAnswered ? 1 : 0.3}
              textStyle="clue"
              textAlign="center"
            >
              {clue.answer}
            </TextFit>
          ) : (
            <motion.div
              key={`value-${clue.id}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2, delay: renderDelay * 0.5 }}
              style={{ width: '100%', height: '100%' }}
            >
              <TextFit
                max={12}
                min={1}
                as={Center}
                textStyle="clueValue"
                textAlign="center"
              >
                ${clue.value}
              </TextFit>
            </motion.div>
          )}
        </AnimatePresence>
      </BoardBox>
    </>
  );
};
